// The shared same-origin POST helper for every routed INTENT the dashboard sends, plus the API
// prefix both it and the read paths are built from. Extracted so the transport modules can share one
// implementation: a mutation is sent once (never retried), carries no Actor/role field (the server
// resolves the Actor — mass-assignment is banned), and a refusal returns the API's SANITISED
// user-facing note, never an internal error or stack (G-197).

import type { RouteResult } from './api-client.ts';

/** The path prefix the dashboard's JSON API is served under (mirrors DASHBOARD_API_PREFIX server-side). */
export const API_PREFIX = '/dashboard/api';

type JsonPrimitive = string | number | boolean | null;
type JsonValue =
  | JsonPrimitive
  | readonly JsonValue[]
  | Readonly<{ [key: string]: JsonValue | undefined }>;

export async function postIntent(
  path: string,
  body: Readonly<Record<string, JsonValue | undefined>>,
): Promise<RouteResult> {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  if (response.ok) return { ok: true };
  // A refused Gate returns a sanitised, user-facing note from the API (no internal detail — G-197).
  const note = await safeNote(response);
  return { ok: false, note };
}

/** Read a sanitised note off a refusal response without ever leaking an internal error (G-197). */
export async function safeNote(response: Response): Promise<string> {
  try {
    const payload = (await response.json()) as { readonly message?: unknown };
    if (typeof payload.message === 'string') return payload.message;
  } catch {
    // A non-JSON body is not surfaced verbatim (it could carry internals) — a generic honest note instead.
  }
  return 'That action was not permitted.';
}
